import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

import {
	getTotalInsuredAmount,
	isEndorsement,
	getBinderNumber,
	getAssured,
	getCountry,
	getAddress,
	getCity,
	getPostalCode,
	getInsuredAmountCurrency,
	getLloydsRiskCode,
	getPolicyFromTransactionObj,
	formatDate,
	getTransactionTypeLondonReport,
	getCustomerType,
	getCommissionRatio,
	getCommissionAmount
} from "./helpers"

import { transformDate } from "@/utils/"

const header = [
	[
		"BDX month",
		"Coverholder Name",
		"Unique Market Reference (UMR)",
		"Reporting Period START DATE",
		"Reporting Period END DATE",
		"Lloyds Risk Code",
		"Hong Kong Regulator Risk Code",
		"Section No (if applicable)",
		"Type of Insurance (Direct or Reinsurance)",
		"Certificate Ref",
		"Insured Full Name, Last Name or Company Name",
		"Insured Address",
		"Insured Postcode, Zip Code or Similar",
		"Insured Country",
		"Risk Inception Date",
		"Risk Expiry Date",
		"Endorsement Effective Date",
		"Endorsement Expiry Date",
		"Location of Risk, Address",
		"Location of Risk, Postcode, zip code or similar",
		"Location of Risk, Country",
		"Transaction Type (New/Renewal/AP/RP/Cancellation)",
		"Original Currency",
		"Gross premium paid this time",
		"Commission %",
		"Commission Amount",
		"Brokerage %",
		"Brokerage Amount",
		"Net Premium",
		"Settlement Currency",
		"Total gross written premium",
		"Sum Insured Currency",
		"Sum Insured Amount",
		"Tax Type",
		"Country of Tax",
		"Tax Currency",
		"Tax Amount",
		"Tax Payable By",
		"Certificate Issuance Date",
		"Customer Type (Consumer/Micro/SME/Commercial/Reinsurer)"
	]
]

/****************** Helper Functions ******************/
const getFilterMonth = filterPayload => {
	const date = transformDate(filterPayload?.issueDate?.after)
	return dayjs(date).format("MMM-YYYY")
}

const getHkRegularRiskCode = type => {
	//   SH - Goods in Transit
	// JB - Property Damage
	// EX - Property Damage
	// TO - Property Damage
	// PJ - Property Damage
	// CB - Property Damage
	// EC - General Liability
	// Excess and PL - Property Damage
	if (type === "sending") return "GIT"
	if (
		type === "jewellers_block" ||
		type === "jewellers_exhibition" ||
		type === "jewellers_tour" ||
		type === "personal_jewellery" ||
		type === "commercial_block" ||
		type === "public_liability"
	)
		return "PD"

	if (type === "employees_compensation") return "GL"
}

const getRiskCountries = policy => {
	const type = policy?.product?.productId?.type
	const assured = getAssured(policy)

	// EX - fact type is location on insured
	if (type === "jewellers_exhibition") {
		return policy?.contractInsured
			?.map(
				item =>
					item.facts?.find(fact => fact.type === "location")?.value?.stringValue
			)
			?.join(",")
	}
	// TO - fact type tourCountry on insured
	if (type === "jewellers_tour") {
		return policy?.contractInsured
			?.map(
				item =>
					item.facts?.find(fact => fact.type === "tourCountry")?.value
						?.stringValue
			)
			?.join(",")
	}
	// SI - fact types are voyageOrigin, voyageVia, voyageDestination on insured
	if (type === "sending") {
		return [
			...policy?.contractInsured?.map(
				item =>
					item.facts?.find(fact => fact.type === "voyageOrigin")?.value
						?.stringValue
			),
			...policy?.contractInsured?.map(
				item =>
					item.facts?.find(fact => fact.type === "voyageVia")?.value
						?.stringValue
			),
			...policy?.contractInsured?.map(
				item =>
					item.facts?.find(fact => fact.type === "voyageDestination")?.value
						?.stringValue
			)
		]?.join(",")
	} else return getCountry(assured)
}

/****************** Define Rows ******************/
const rows = (fetchedData, filterPayload) => {
	let output = []
	fetchedData?.reverse()?.map(transaction => {
		const policy = getPolicyFromTransactionObj(transaction)
		const sectionNumber = getBinderNumber(policy)?.match(/\((.*?)\)/)?.[1]
		const netPremium =
			transaction?.amount -
			getCommissionAmount("coverholder", transaction?.amount, policy) -
			getCommissionAmount("broker", transaction?.amount, policy)
		const assured = getAssured(policy)

		output.push([
			getFilterMonth(filterPayload),
			"",
			getBinderNumber(policy),
			formatDate(filterPayload?.issueDate?.after),
			formatDate(filterPayload?.issueDate?.before),
			getLloydsRiskCode(policy),
			getHkRegularRiskCode(policy?.product?.productId?.type),
			sectionNumber,
			"Direct",
			policy?.issuerNumber,
			assured?.name,
			getAddress(assured),
			getCity(assured),
			getCountry(assured),
			policy?.startDate ? formatDate(policy.startDate) : "",
			policy?.endDate ? formatDate(policy.endDate) : "",
			isEndorsement(transaction) && policy?.startDate
				? formatDate(policy.startDate)
				: "",
			isEndorsement(transaction) && policy?.endDate
				? formatDate(policy.endDate)
				: "",
			getAddress(assured),
			getCity(assured),
			getRiskCountries(policy), // risk countries
			getTransactionTypeLondonReport(transaction),
			transaction?.currencyCode,
			transaction?.amount,
			getCommissionRatio("coverholder", policy) * 100,
			getCommissionAmount("coverholder", transaction?.amount, policy),
			getCommissionRatio("broker", policy) * 100,
			getCommissionAmount("broker", transaction?.amount, policy),
			netPremium,
			transaction?.currencyCode,
			transaction?.amount,
			getInsuredAmountCurrency(policy),
			getTotalInsuredAmount(policy),
			"",
			"",
			"",
			"",
			"",
			formatDate(policy?.issueDate),
			getCustomerType(policy)
		])
	})

	return output
}

/****************** Function for fetching ******************/
const fetchTransactions = async filterPayload => {
	const dateFrom = filterPayload?.issueDate?.after
		? dayjs(filterPayload?.issueDate?.after).format()
		: null
	const dateTo = filterPayload?.issueDate?.before
		? dayjs(filterPayload?.issueDate?.before)
				.add(1, "day")
				.format()
		: null

	const query = gql`
		fragment obj on policy {
			id
			events {
				timestamp
				type
			}
			proposal {
				renewalHistory {
					renewedFrom {
						id
					}
				}
			}
			stakeholders {
				entity {
					internalCode
				}
				type
			}
			associatedContracts {
				contract {
					startDate
					endDate
					facts {
						id
						type
						value {
							stringValue
						}
					}
				}
			}
			facts {
				id
				type
				value {
					stringValue
					numberValue
					dateValue
				}
			}
			contractHolder {
				__typename
				name
				addresses {
					type
					fields2 {
						type
						value
					}
				}
			}
			contractInsured {
				__typename
				name
				addresses {
					type
					fields2 {
						type
						value
					}
				}
				facts {
					type
					value {
						stringValue
					}
				}
			}
			startDate
			endDate
			transactions {
				amount
				facts {
					type
					value {
						stringValue
					}
				}
			}
			commissionSplitRules {
				type
				currencyCode
				amount
				remark
				ratio
			}
			premium {
				currencyCode
				amount
				originalPrice
			}
			valuesJsonString
			issueDate
			product {
				productId {
					type
				}
				pricing {
					currencyCode
				}
			}
			issuerNumber
		}
		query fetchTransactions($transactionWhere: transactionWhereInput) {
			transactions(where: $transactionWhere) {
				list {
					amount
					currencyCode
					dateTime
					status
					object {
						__typename
						... on policy {
							...obj
							endorsements {
								beforeEndorsement {
									...obj
								}
							}
						}
						... on endorsement {
							afterEndorsement {
								...obj
							}
						}
					}
				}
			}
		}
	`
	const variables = {
		transactionWhere: {
			and: [
				{ dateTime_gt: dateFrom },
				{ dateTime_lt: dateTo },
				{
					or: [
						{ status: "APPROVED" },
						{ status: "REFUNDED" },
						{ status: "CANCELLED_BY_USER" }
					]
				}
			]
		}
	}

	const res = await api({ query, variables })
	//TODO: error handling
	return res?.data?.transactions?.list
}

/****************** Export ******************/
export default async filterPayload => {
	const fetchedData = await fetchTransactions(filterPayload)
	const workSheetData = [...header, ...rows(fetchedData, filterPayload)]
	const workSheetColumns = header?.[0]?.map(item => ({ wpx: 200 }))
	return { workSheetData, workSheetColumns }
}
